import { allExciseTaxTypes, invoiceStrings } from '@data/invoice';
import { country } from '@models/Country';
import { invoiceCurrency } from '@models/Invoice2';
import { requiredNumberField, requiredStringField, stringField } from '@utils/forms';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const exciseTaxType = t.union([
  t.literal(allExciseTaxTypes[0]),
  t.literal(allExciseTaxTypes[1]),
  t.literal(allExciseTaxTypes[2]),
  t.literal(allExciseTaxTypes[3]),
]);

export const invoiceDetailCanEdit = t.union([t.literal('cn'), t.literal('jp'), t.literal('none')]);

export type InvoiceDetailCanEdit = t.TypeOf<typeof invoiceDetailCanEdit>;

export const baseInvoiceDetail = t.type({
  expense_cost: t.number,
  invoice_name: t.string,
  unit_num_type: t.string,
  currency: invoiceCurrency,
  unit_price: h.omittableNumber().cast(),
  unit_num: h.omittableNumber().cast(),
  excise_tax_type: exciseTaxType,
  /**
   * The "country" of the invoice detail.
   * Notice that all CN data can be managed by JP, but not the other way.
   * Therefore:
   * 'none' can be written by no one.
   * 'cn' can be written by salesperson_cn & salesperson_jp.
   * 'jp' can only be written by salesperson_jp.
   */
  country: invoiceDetailCanEdit,
  payer_mark: country,
});

export type BaseInvoiceDetail = t.TypeOf<typeof baseInvoiceDetail>;

export const invoiceDetail = t.type({
  ...baseInvoiceDetail.props,
  id: t.number,
  invoice_name_preparation: t.string,
  updated_at: t.string,
  created_at: t.string,
  invoice: h.omittable(t.number),
  is_locked: t.boolean,
});

export type InvoiceDetail = t.TypeOf<typeof invoiceDetail>;

export const invoiceDetailPages = t.type({
  count: t.number,
  next: h.omittable(t.string),
  previous: h.omittable(t.string),
  results: t.array(invoiceDetail),
});

export const invoiceDetailPostBody = t.type({
  expense_cost: t.number,
  invoice_name: t.string,
  invoice_name_preparation: t.string,
  unit_price: t.number,
  unit_num: t.number,
  unit_num_type: t.string,
  excise_tax_type: t.string,
});

export type InvoiceDetailPostBody = t.TypeOf<typeof invoiceDetailPostBody>;

export const invoiceDetailPatchBody = t.partial({
  ...invoiceDetailPostBody.props,
  is_locked: t.boolean,
});

export type InvoiceDetailPatchBody = t.TypeOf<typeof invoiceDetailPatchBody>;

export type InvoiceSendPostBody = t.TypeOf<typeof invoiceSendPostBody>;

export const invoiceSendPostBody = t.type({
  send_to: h.omittable(t.string),
  send_method: h.omittable(t.string),
});

export type InvoiceQueryParam = t.TypeOf<typeof invoiceQueryParam>;

export const invoiceQueryParam = t.partial({
  page: h.number().cast(),
  page_size: h.number().cast(),
  booking_inquiry: h.number().cast(),
  house_bl_number: t.string,
  d2d_charge_no: t.string,
  ordering: t.string,
  charge_pay_day_before: t.string,
  charge_pay_day_after: t.string,
  payment_status: t.string,
  charge_date_before: t.string,
  charge_date_after: t.string,
  salesperson_name: t.array(t.string),
  invoice_status: t.array(t.string),
  member_salesperson_id: h.number().cast(),
  salesperson_japan_id: h.number().cast(),
  company_name: t.string,
  customer_no: t.string,
  shipper_name: t.string,
  consignee_name: t.string,
  quantity: t.string,
  service_name: t.array(t.string),
  total_price_min: h.number().cast(),
  total_price_max: h.number().cast(),
  total_price_currency: h.omittable(invoiceCurrency),
  preferred_arrival_date_before: t.string,
  preferred_arrival_date_after: t.string,
  d2d_no: t.string,
});

export const invoiceDetailForm = t.type({
  invoice_name: requiredStringField(invoiceStrings.invoice_name),
  invoice_name_preparation: stringField(invoiceStrings.invoice_name_preparation),
  unit_price: requiredNumberField(),
  currency: requiredStringField(invoiceStrings.currency),
  unit_num: requiredNumberField(invoiceStrings.unit_num),
  unit_num_type: requiredStringField(invoiceStrings.unit_num_type),
  excise_tax_type: requiredStringField(invoiceStrings.excise_tax_type),
  payer_mark: country,
});

export type InvoiceDetailForm = t.TypeOf<typeof invoiceDetailForm>;

export const mapInvoiceDetailToInvoiceDetialForm = (v: InvoiceDetail): InvoiceDetailForm => {
  return {
    ...v,
    ...(v.unit_price == null && {
      unit_price: '' as any,
    }),
    ...(v.unit_num == null && {
      unit_num: '' as any,
    }),
  };
};

export const invoiceDetailFormInitialValues: InvoiceDetailForm = {
  invoice_name: '',
  invoice_name_preparation: '',
  unit_price: '' as any,
  currency: 'JPY',
  unit_num: '' as any,
  unit_num_type: '',
  excise_tax_type: '',
  payer_mark: 'jp',
};
